import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Modal,
  Button,
  TextDescription,
  Typography,
} from "@ann/web-components";
import {
  ModalContent,
  FormField,
  CharacterCount,
} from "./EditDescriptionModal.styled";
import { useMediaQueries } from "../../../../../../../utils/mediaQueries";
import { updateFact } from "../../../../../../../services";
import { useToast } from "../../../../../../../components/toast";

const EditDescriptionModal = ({ isOpen, onClose, factData, onSave }) => {
  const toast = useToast();
  const { factId } = useParams();
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();

  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return "95%";
    }
    return "500px";
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  useEffect(() => {
    if (factData && isOpen) {
      setDescription(factData.factDescription || "");
    }
  }, [factData, isOpen]);

  const handleDescriptionChange = useCallback((value) => {
    setDescription(value);
  }, []);

  const characterCount = description.length;

  const handleSave = useCallback(async () => {
    // Get fact_uuid from URL (factId param) or factData as fallback
    const factUuid = factId || factData?.fact_uuid || factData?.id || factData?.uuid;
    if (!factUuid) {
      toast.error({
        title: "Missing fact UUID",
        message: "fact_uuid is required to update a fact. Please ensure you're on a valid fact page.",
        canClose: true,
      });
      return;
    }

    // Build payload
    const payload = {
      fact_uuid: factUuid,
      description: description.trim(),
    };

    try {
      setIsLoading(true);

      const response = await updateFact(payload);

      // Backend returns { statusCode, msgCode, body } even on HTTP 200
      if (response?.statusCode !== 200) {
        const code = response?.msgCode?.[0]?.code || "ERROR";
        const description =
          response?.msgCode?.[0]?.description || "Failed to update fact description.";
        toast.error({ title: code, message: description, canClose: true });
        return;
      }

      toast.success({
        title: "Description updated",
        message: response?.msgCode?.[0]?.description || "Fact description updated successfully.",
        canClose: true,
      });

      if (onSave) {
        onSave(response?.body || { description });
      }

      // Close modal
      onClose();
    } catch (error) {
      const apiErr = error?.response?.data;
      const code = apiErr?.msgCode?.[0]?.code || apiErr?.msgCode || "Error";
      const description = apiErr?.msgCode?.[0]?.description || apiErr?.error || "An error occurred while updating the fact description.";

      toast.error({
        title: code,
        message: description,
        canClose: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [description, onSave, onClose, factData, factId, toast]);

  const handleCancel = useCallback(() => {
    setDescription(factData?.factDescription || "");
    onClose();
  }, [onClose, factData]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="medium"
      width={modalWidth}
    >
      <Modal.Header>
        <Modal.Header.Title>Edit Description</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <FormField>
            <Typography category="label" size="small" weight="bold">
              Description
            </Typography>
            <TextDescription
              label=""
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter the fact description"
              height="200px"
              size="small"
            />
            <CharacterCount>
              <Typography
                category="body"
                size="small"
                weight="regular"
                color="subTitle"
              >
                {characterCount} characters
              </Typography>
            </CharacterCount>
          </FormField>
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Button
          label="Cancel"
          size="medium"
          type="secondary"
          onClick={handleCancel}
          disabled={isLoading}
        />
        <Button
          label="Save Changes"
          size="medium"
          type="primary"
          onClick={handleSave}
          disabled={isLoading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default EditDescriptionModal;

